import moment from "moment";
import { dayToNumber, sampleStudentLink, sampleStudentMixedLink, sampleTeacherLink } from "../../constants/common";
import dayjs from "dayjs";
import axios from "axios";
import CryptoJS from "crypto-js";

export const getRandomColor = () => {
  const colors = [
    "#007bff",
    "#28a745",
    "#dc3545",
    "#ffc107",
    "#17a2b8",
    "#6f42c1",
    "#fd7e14",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

export const createAntdSelection = (data = [], label = "name", value = "id") =>
  data?.map((item) => ({
    label: item?.[label],
    value: item?.[value],
  }));

export const handleRedirect = (baseUrl, paramName, paramValue, secretKey) => {
  const encrypted = CryptoJS.AES.encrypt(paramValue, secretKey).toString();
  const url = `${baseUrl}?${encodeURIComponent(paramName)}=${encodeURIComponent(encrypted)}`;
  window.location.href = url;
};

export const extractFileName = (url) => {
  return url
    ? decodeURIComponent(url)?.split("#")?.slice(-1)?.[0] ?? null
    : null;
};

export function getThisWeeksEvents(slots) {
  const startOfWeek = moment().startOf("week"); // Sunday

  return slots.map((slot) => {
    const dayIndex = dayToNumber[slot.day];

    // Calculate base date for the weekday
    const baseDate = moment(startOfWeek).add(dayIndex, "days");

    const startTime = moment(slot.start_time, "h:mmA");
    const endTime = moment(slot.end_time, "h:mmA");

    const start = moment(baseDate).set({
      hour: startTime.hour(),
      minute: startTime.minute(),
      second: 0,
    });

    const end = moment(baseDate).set({
      hour: endTime.hour(),
      minute: endTime.minute(),
      second: 0,
    });

    return {
      title: slot.title,
      start: start.toDate(),
      end: end.toDate(),
    };
  });
}

export const WEEK_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const getNextMonday = (date) => {
  const dayIdx = dayjs(date).toDate().getDay();
  const toBeAddedDays = 6 - dayIdx + 2;
  return dayjs(date).add(toBeAddedDays, "days");
};

export const getSameWeekMonday = (date) => {
  const dayIdx = dayjs(date).toDate().getDay();
  const mondayDate = !dayIdx
    ? dayjs(date).add(1, "days")
    : dayjs(date).subtract(dayIdx - 1, "days");
  return mondayDate;
};

export const getMondayToSaturDayOfWeek = (date) => {
  return Array.from({ length: 5 }).map((_, i) => {
    return dayjs(getSameWeekMonday(date)).add(i, "days");
  });
};

export const getImageName = (img) => {
  const imageName = img
    ? decodeURIComponent(img)
      ?.split("#")
      ?.slice(-1)?.[0] ?? null
    : null;
  return imageName ? { name: imageName } : {}
};

export const refetchSchool = () => {
  window.dispatchEvent(new CustomEvent("refetch-school"));
};

export const getOrdinalSuffix = (number) => {
  if (typeof number !== "number" || isNaN(number)) return "";

  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${number}th`;
  }

  switch (lastDigit) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

export const downloadFile = (blobData, contentDisposition, defaultFileName = "download.csv") => {
  let filename = defaultFileName;

  if (contentDisposition) {
    // Handles: filename=students.csv OR filename="students.csv" OR filename*=UTF-8''students.csv
    const match = contentDisposition.match(/filename\*?=(?:UTF-\d''|")?([^;\r\n"]+)/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1].replace(/["']/g, ""));
    }
  }

  const blob = new Blob([blobData], { type: blobData.type || "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadS3File = async (fileUrl, fileName) => {
  try {
    const resp = await axios.get(fileUrl, {
      responseType: "blob",
    });
    const blob = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.download = fileName ? fileName : extractFileName(fileUrl);
    a.href = blob;
    document.body.appendChild(a);
    a.click();
  } catch (err) {
    console.log(err);
  }
};

export const waitForImagesToLoad = async (container) => {
  const images = container.querySelectorAll("img");
  const promises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });
  return Promise.all(promises);
};

export const convertAllImagesToBase64 = async (container) => {
  const images = container.querySelectorAll("img");

  for (const img of images) {
    const url = img.src;

    // Skip if already base64
    if (url.startsWith("data:")) continue;

    try {
      if (url.endsWith(".svg") || url.includes(".svg?")) {
        const base64 = await convertSvgToBase64Png(url);
        if (base64) img.setAttribute("src", base64);
      } else {
        const response = await fetch(url, {
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        });

        const blob = await response.blob();
        const reader = new FileReader();

        const base64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        img.setAttribute("src", base64);
      }
    } catch (error) {
      console.warn("Image to base64 conversion failed:", url, error);
    }
  }
};

const convertSvgToBase64Png = async (svgUrl) => {
  try {
    const response = await fetch(svgUrl, { cache: "no-cache" });
    const svgText = await response.text();

    const blob = new Blob([svgText], { type: "image/svg+xml" });
    const objectUrl = URL.createObjectURL(blob);

    const tempImg = new Image();
    tempImg.crossOrigin = "anonymous";
    tempImg.src = objectUrl;

    await new Promise((resolve, reject) => {
      tempImg.onload = resolve;
      tempImg.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    canvas.width = tempImg.width || 300;
    canvas.height = tempImg.height || 150;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(tempImg, 0, 0);

    const base64 = canvas.toDataURL("image/png");

    URL.revokeObjectURL(objectUrl);
    return base64;
  } catch (err) {
    console.warn("SVG to base64 PNG conversion failed:", svgUrl, err);
    return null;
  }
};

export const preventElementSplitting = (container) => {
  if (!container) return;

  const noBreakElements = container.querySelectorAll(
    ".report-section, .image-wrapper, img, table, tr"
  );

  noBreakElements.forEach((el) => {
    el.style.pageBreakInside = "avoid";
    el.style.breakInside = "avoid";
  });
};

export const checkMixedSchool = (user) => {
  return user?.institution?.boarding_or_day === "DAY/BOARDING";
};

export const getSampleFileConfig = ({
  isStudentPage,
  isMixedSchool,
}) => {
  if (isStudentPage) {
    return {
      sampleFileName: "sample_students.xlsx",
      sampleFileLink: isMixedSchool ? sampleStudentMixedLink : sampleStudentLink,
    };
  } else {
    return {
      sampleFileName: sampleTeacherLink.split("/").pop(),
      sampleFileLink: sampleTeacherLink,
    };
  }
};

export const searchLocations = async (query, limit = 5) => {
  if (!query) return [];

  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?` +
      new URLSearchParams({
        q: query,
        format: "json",
        addressdetails: 1,
        limit,
      }),
    {
      headers: {
        "Accept": "application/json",
      },
    }
  );

  return res.json();
};